import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Field, TextInput } from "@/components/Field";
import { PrimaryButton } from "@/components/primitives";
import { formatApiError } from "@/lib/api";
import { ASSETS, PRACTITIONER } from "@/data/content";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ready && user) nav("/admin");
  }, [ready, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Isabelle.");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-lavender-soft/40 px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={ASSETS.logo} alt="" className="h-12 w-12 rounded-full object-cover" />
          <span className="font-heading text-2xl text-brand-ink">{PRACTITIONER.business}</span>
        </Link>
        <div className="rounded-3xl border border-brand-line bg-white p-8 md:p-10 shadow-[0_20px_60px_rgba(59,92,69,0.1)]">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-11 w-11 rounded-full bg-brand-forest/10 flex items-center justify-center">
              <Lock className="text-brand-forest" size={18} strokeWidth={1.5} />
            </span>
            <div>
              <h1 className="font-heading text-2xl text-brand-ink leading-none">Practitioner login</h1>
              <p className="font-body text-sm text-brand-slate mt-1">Manage your enquiries & bookings</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
            <Field label="Email">
              <TextInput data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
            </Field>
            <Field label="Password">
              <TextInput data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </Field>
            <PrimaryButton type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </PrimaryButton>
          </form>
        </div>
        <p className="text-center mt-6 font-body text-sm text-brand-slate">
          <Link to="/" className="hover:text-brand-forest">← Back to the website</Link>
        </p>
      </div>
    </div>
  );
}
